import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../services/api.js';
import NavBar from '../components/NavBar.jsx';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator.jsx';
import { usePullToRefresh } from '../hooks/usePullToRefresh.js';

const AVATAR_MAP = {
  'astronaut': '🧑‍🚀', 'scientist': '👩‍🔬', 'wizard': '🧙', 'hero': '🦸',
  'cat': '🐱', 'dog': '🐶', 'fox': '🦊', 'panda': '🐼', 'lion': '🦁',
  'frog': '🐸', 'octopus': '🐙', 'unicorn': '🦄', 'dragon': '🐲', 'robot': '🤖',
};
const getAvatar = (a) => AVATAR_MAP[a] || a || '🧑‍🚀';

const TABS = ['mes-amis', 'demandes', 'ajouter'];

export default function Friends() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('mes-amis');
  const [friends, setFriends] = useState([]);
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);

  // Recherche
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);

  // Défi d'un ami (choix du thème inline)
  const [challengeFriend, setChallengeFriend] = useState(null);
  const [themes, setThemes] = useState([]);
  const [themesLoading, setThemesLoading] = useState(false);
  const [challenging, setChallenging] = useState(false);
  const [toast, setToast] = useState(null);

  const loadData = async () => {
    try {
      const [f, r] = await Promise.all([api.getFriends(), api.getFriendRequests()]);
      setFriends(f || []);
      setReceived(r?.received || []);
      setSent(r?.sent || []);
    } catch (e) { /* silencieux */ }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const { pullDistance, isRefreshing, threshold, bindProps } = usePullToRefresh(loadData);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  // ── Recherche (débounce) ──
  useEffect(() => {
    if (searchRef.current) clearTimeout(searchRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    searchRef.current = setTimeout(async () => {
      try {
        const res = await api.searchUsers(q);
        setResults(res || []);
      } catch (e) { setResults([]); }
      setSearching(false);
    }, 350);
    return () => searchRef.current && clearTimeout(searchRef.current);
  }, [query]);

  const patchResultRelation = (userId, relation) =>
    setResults(rs => rs.map(u => u.id === userId ? { ...u, relation } : u));

  const handleAdd = async (u) => {
    try {
      const res = await api.sendFriendRequest(u.id);
      patchResultRelation(u.id, res.status === 'accepted' ? 'friends' : 'sent');
      flash(res.status === 'accepted' ? `Vous êtes amis avec ${u.display_name} !` : `Demande envoyée à ${u.display_name}`);
      loadData();
    } catch (e) { flash(e.message); }
  };

  const handleAccept = async (req) => {
    try { await api.acceptFriendRequest(req.request_id); loadData(); }
    catch (e) { flash(e.message); }
  };
  const handleDecline = async (req) => {
    try { await api.declineFriendRequest(req.request_id); loadData(); }
    catch (e) { flash(e.message); }
  };
  const handleRemove = async (u) => {
    if (!window.confirm(`Retirer ${u.display_name} de tes amis ?`)) return;
    try { await api.removeFriend(u.id); loadData(); }
    catch (e) { flash(e.message); }
  };

  // ── Défier un ami ──
  const openChallenge = async (friend) => {
    setChallengeFriend(friend);
    setThemes([]);
    setThemesLoading(true);
    try {
      const t = await api.getDuelThemesWith(friend.id);
      setThemes(t || []);
    } catch (e) { setThemes([]); }
    setThemesLoading(false);
  };
  const doChallenge = async (theme) => {
    if (challenging) return;
    setChallenging(true);
    try {
      await api.createDuel({ opponent_id: challengeFriend.id, theme_id: theme.id });
      setChallengeFriend(null);
      navigate('/duels');
    } catch (e) { flash(e.message); setChallenging(false); }
  };

  if (loading) {
    return <div className="loading-screen"><div className="loading-rocket">👥</div><p>Chargement...</p></div>;
  }

  const relationLabel = {
    friends: '✓ Ami',
    sent: '⏳ Envoyée',
    received: '↩️ T\'a ajouté',
  };

  return (
    <motion.div className="duels-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <NavBar />
      <div className="duels-content" {...bindProps}>
        <PullToRefreshIndicator distance={pullDistance} threshold={threshold} loading={isRefreshing} />

        {toast && <div className="friend-toast">{toast}</div>}

        <div className="duels-tabs">
          {TABS.map(t => (
            <button key={t} className={`duels-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
              {t === 'mes-amis' ? '👥 Mes amis' :
               t === 'demandes' ? `📩 Demandes${received.length ? ` (${received.length})` : ''}` :
               '➕ Ajouter'}
            </button>
          ))}
        </div>

        {/* ═══ MES AMIS ═══ */}
        {tab === 'mes-amis' && (
          <div className="duels-list">
            {friends.map(f => (
              <div key={f.id} className="duel-card">
                <div className="duel-card-info">
                  <span className="duel-avatar">{getAvatar(f.avatar)}</span>
                  <div>
                    <strong>{f.display_name}</strong>
                    <div className="duel-elo">@{f.username}</div>
                    <div className="friend-stats">🏆 {(f.total_points || 0).toLocaleString('fr-FR')} pts · ⚔️ {f.elo_rating} ELO</div>
                  </div>
                </div>
                <div className="duel-card-actions">
                  <button className="btn-play" onClick={() => openChallenge(f)}>⚔️ Défier</button>
                  <button className="btn-decline" title="Retirer" onClick={() => handleRemove(f)}>✕</button>
                </div>
              </div>
            ))}
            {friends.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">👥</div>
                <p>Aucun ami pour l'instant</p>
                <button className="btn-login" onClick={() => setTab('ajouter')}>Ajouter un ami</button>
              </div>
            )}
          </div>
        )}

        {/* ═══ DEMANDES ═══ */}
        {tab === 'demandes' && (
          <div className="duels-list">
            {received.length > 0 && (
              <div className="duel-section">
                <h3>📩 Reçues</h3>
                {received.map(r => (
                  <div key={r.request_id} className="duel-card pending">
                    <div className="duel-card-info">
                      <span className="duel-avatar">{getAvatar(r.avatar)}</span>
                      <div>
                        <strong>{r.display_name}</strong>
                        <div className="duel-elo">@{r.username} · ELO {r.elo_rating}</div>
                      </div>
                    </div>
                    <div className="duel-card-actions">
                      <button className="btn-accept" onClick={() => handleAccept(r)}>✅ Accepter</button>
                      <button className="btn-decline" onClick={() => handleDecline(r)}>❌</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {sent.length > 0 && (
              <div className="duel-section">
                <h3>⏳ Envoyées</h3>
                {sent.map(r => (
                  <div key={r.request_id} className="duel-card waiting">
                    <div className="duel-card-info">
                      <span className="duel-avatar">{getAvatar(r.avatar)}</span>
                      <div>
                        <strong>{r.display_name}</strong>
                        <div className="duel-status">En attente de sa réponse</div>
                      </div>
                    </div>
                    <button className="btn-decline" title="Annuler" onClick={() => handleDecline(r)}>✕</button>
                  </div>
                ))}
              </div>
            )}

            {received.length === 0 && sent.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">📭</div>
                <p>Aucune demande en attente</p>
              </div>
            )}
          </div>
        )}

        {/* ═══ AJOUTER ═══ */}
        {tab === 'ajouter' && (
          <div className="challenge-flow">
            <h3>Cherche un ami par son pseudo</h3>
            <input
              className="friend-search-input"
              type="text"
              placeholder="Pseudo ou nom…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
            />
            {query.trim().length > 0 && query.trim().length < 2 && (
              <p className="no-opponents">Tape au moins 2 lettres.</p>
            )}
            {searching && <div className="theme-picker-loading">⏳ Recherche…</div>}
            <div className="duels-list">
              {results.map(u => (
                <div key={u.id} className="duel-card">
                  <div className="duel-card-info">
                    <span className="duel-avatar">{getAvatar(u.avatar)}</span>
                    <div>
                      <strong>{u.display_name}</strong>
                      <div className="duel-elo">@{u.username} · ELO {u.elo_rating}</div>
                    </div>
                  </div>
                  {u.relation === 'none' || u.relation === 'received' ? (
                    <button className="btn-accept" onClick={() => handleAdd(u)}>
                      {u.relation === 'received' ? '✅ Accepter' : '➕ Ajouter'}
                    </button>
                  ) : (
                    <span className="friend-rel-tag">{relationLabel[u.relation]}</span>
                  )}
                </div>
              ))}
              {!searching && query.trim().length >= 2 && results.length === 0 && (
                <p className="no-opponents">Aucun joueur trouvé pour « {query.trim()} ».</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ═══ Modale : choix du thème pour défier un ami ═══ */}
      {challengeFriend && (
        <div className="friend-modal-overlay" onClick={() => !challenging && setChallengeFriend(null)}>
          <div className="friend-modal" onClick={e => e.stopPropagation()}>
            <h3>⚔️ Défier {challengeFriend.display_name}</h3>
            {themesLoading ? (
              <div className="theme-picker-loading">⏳ Recherche des thèmes communs…</div>
            ) : themes.length === 0 ? (
              <div className="theme-picker-empty">
                😕 Aucun thème commun avec {challengeFriend.display_name} pour l'instant.
                Débloquez les mêmes thèmes pour vous défier !
              </div>
            ) : (
              <div className="theme-picker">
                {themes.map(t => (
                  <div key={t.id}
                    className="theme-pick"
                    onClick={() => doChallenge(t)}
                    style={challenging ? { opacity: 0.4, pointerEvents: 'none' } : undefined}>
                    {t.icon} {t.name}
                  </div>
                ))}
              </div>
            )}
            <button className="btn-decline" style={{ marginTop: 16 }} onClick={() => !challenging && setChallengeFriend(null)}>
              Annuler
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
}
